import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  FaTrash,
  FaCheckCircle,
  FaCircle,
  FaSave,
  FaEdit,
} from "react-icons/fa";

const SKUVariantManagement = ({
  formData,
  setFormData,
  onSave,
  setIsSkuSaved,
}) => {
  const initialData = formData?.SkuDimension;

  const [dimensions, setDimensions] = useState([]);
  const [dimensionValues, setDimensionValues] = useState({});
  const [currentDimension, setCurrentDimension] = useState("");
  const [variants, setVariants] = useState([]);
  const [isEditMode, setIsEditMode] = useState(true);

  // Initialize dimensions and dimensionValues from JSON
  useEffect(() => {
    if (initialData) {
      const dimensionsFromData = initialData?.SkuDimension?.map(
        (dim) => dim.name
      );
      const valuesFromData = initialData?.SkuDimension?.reduce((acc, dim) => {
        acc[dim.name] = dim.variants;
        return acc;
      }, {});

      setDimensions(dimensionsFromData);
      setDimensionValues(valuesFromData);
      generateVariants(valuesFromData); // Generate variants after loading initial data
    }
  }, []);

  const addDimension = () => {
    if (!currentDimension.trim()) return;

    if (!dimensions.includes(currentDimension)) {
      setDimensions([...dimensions, currentDimension]);
      setDimensionValues({ ...dimensionValues, [currentDimension]: [] });
      setIsSkuSaved(false); // Reset save status
    }
    setCurrentDimension("");
  };

  const addDimensionValue = (dimension, value) => {
    if (!value.trim()) return;

    const newValues = value.split(",").map((val) => {
      const [dimensionValue, price] = val.split(":").map((item) => item.trim());
      return { value: dimensionValue, price: price || "" };
    });

    const updatedValues = {
      ...dimensionValues,
      [dimension]: [...(dimensionValues[dimension] || []), ...newValues],
    };
    setDimensionValues(updatedValues);
    generateVariants(updatedValues);
    setIsSkuSaved(false); // Reset save status
  };

  const generateVariants = (updatedValues) => {
    const keys = Object.keys(updatedValues);
    if (keys.length === 0) return;

    const combinations = keys.reduce((acc, key) => {
      if (acc.length === 0) {
        return updatedValues[key].map(({ value, price }) => ({
          [key]: value,
          active: true,
          price,
        }));
      }
      return acc.flatMap((existing) =>
        updatedValues[key].map(({ value, price }) => ({
          ...existing,
          [key]: value,
          active: true,
          price,
        }))
      );
    }, []);

    setVariants(combinations);
  };

  const toggleVariantStatus = (variantIndex) => {
    const updatedVariants = variants.map((variant, index) => {
      if (index === variantIndex) {
        return { ...variant, active: !variant.active };
      }
      return variant;
    });
    setVariants(updatedVariants);
  };

  const updateVariantPrice = (variantIndex, price) => {
    const updatedVariants = variants.map((variant, index) => {
      if (index === variantIndex) {
        return { ...variant, price };
      }
      return variant;
    });
    setVariants(updatedVariants);
  };

  const deleteVariant = (variantIndex) => {
    const updatedVariants = [...variants];
    updatedVariants.splice(variantIndex, 1);
    setVariants(updatedVariants);
  };

  const deleteDimension = (dimension) => {
    const updatedDimensions = dimensions.filter((dim) => dim !== dimension);
    const { [dimension]: _, ...updatedValues } = dimensionValues;

    setDimensions(updatedDimensions);
    setDimensionValues(updatedValues);
    generateVariants(updatedValues);
    setIsSkuSaved(false); // Reset save status
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedVariants = Array.from(variants);
    const [removed] = reorderedVariants.splice(result.source.index, 1);
    reorderedVariants.splice(result.destination.index, 0, removed);

    setVariants(reorderedVariants);
  };

  const saveSKUs = () => {
    const skuDimension = dimensions.map((dimension) => ({
      name: dimension,
      variants: dimensionValues[dimension]?.map(({ value, price }) => ({
        name: value,
        price: price || 0,
      })),
    }));

    if (onSave) {
      onSave(skuDimension); // Pass data to parent
    }
    setIsEditMode(false);
  };

  const toggleEditMode = () => {
    setIsEditMode(true);
    setIsSkuSaved(false);
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">
          SKU Variant Management
        </h1>

        {/* Dimension Management */}
        {isEditMode && (
          <div className="mb-8">
            <label className="block text-gray-700 font-medium mb-4 text-lg">
              Add Dimension
            </label>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Enter dimension name (e.g., Size)"
                className="border border-gray-300 rounded-lg p-3 flex-1 shadow-sm focus:ring focus:ring-blue-300 focus:outline-none"
                value={currentDimension}
                onChange={(e) => setCurrentDimension(e.target.value)}
              />
              <button
                onClick={addDimension}
                className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow hover:bg-blue-600 flex items-center gap-2 text-lg"
              >
                Add Dimension
              </button>
            </div>
          </div>
        )}

        {dimensions.map((dimension) => (
          <div key={dimension} className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-xl text-gray-800 capitalize">
                {dimension}
              </h2>
              {isEditMode && (
                <button
                  onClick={() => deleteDimension(dimension)}
                  className="bg-red-500 text-white px-3 py-2 rounded-full shadow hover:bg-red-600"
                >
                  <FaTrash />
                </button>
              )}
            </div>
            {isEditMode && (
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <input
                    type="text"
                    placeholder={`Add values for ${dimension} (e.g., SM:80,MD:100,LG:120)`}
                    className="border border-gray-300 rounded-lg p-3 flex-1 shadow-sm focus:ring focus:ring-blue-300 focus:outline-none"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        addDimensionValue(dimension, e.target.value);
                        e.target.value = "";
                      }
                    }}
                  />
                </div>
                <div className="flex flex-wrap gap-4">
                  {dimensionValues[dimension]?.map(
                    ({ value, price }, index) => (
                      <div
                        key={index}
                        className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm shadow-md flex items-center gap-3"
                      >
                        {value}{" "}
                        {price && (
                          <span className="text-gray-500">(₹{price})</span>
                        )}
                        {isEditMode && (
                          <button
                            onClick={() => {
                              const updatedValues = {
                                ...dimensionValues,
                                [dimension]: dimensionValues[dimension].filter(
                                  (v, i) => i !== index
                                ),
                              };
                              setDimensionValues(updatedValues);
                              generateVariants(updatedValues);
                            }}
                            className="text-red-500 hover:text-red-600"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {variants.length > 0 && (
          <div className="mt-10">
            <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
              Generated Variants
            </h2>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="variants">
                {(provided) => (
                  <ul
                    className="space-y-2"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {variants.map((variant, index) => (
                      <Draggable
                        key={index}
                        draggableId={`variant-${index}`}
                        index={index}
                      >
                        {(provided) => (
                          <li
                            className={`p-3 border rounded-lg shadow-md hover:shadow-lg transition-shadow flex justify-between items-center ${
                              variant.active
                                ? "bg-gray-50 border-gray-200"
                                : "bg-gray-200 border-gray-400"
                            }`}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <div className="flex gap-4 items-center w-full">
                              <button
                                onClick={() => toggleVariantStatus(index)}
                                className={`${
                                  variant.active
                                    ? "bg-green-500"
                                    : "bg-gray-400"
                                } text-white px-3 py-2 rounded-lg text-sm hover:opacity-90 flex items-center gap-2`}
                                {...provided.dragHandleProps}
                              >
                                {variant.active ? (
                                  <FaCheckCircle />
                                ) : (
                                  <FaCircle />
                                )}
                              </button>
                              <div className="flex-1 grid grid-cols-3 gap-2">
                                {Object.entries(variant).map(
                                  ([key, value]) =>
                                    key !== "active" &&
                                    key !== "price" && (
                                      <p
                                        key={key}
                                        className="text-sm text-gray-700 capitalize"
                                      >
                                        <span className="font-medium">
                                          {key}:
                                        </span>{" "}
                                        {value}
                                      </p>
                                    )
                                )}
                                <div className="flex items-center gap-2">
                                  <label className="text-sm text-gray-700 font-medium">
                                    ₹
                                  </label>
                                  <input
                                    type="number"
                                    placeholder="Price in INR"
                                    value={variant.price || 0}
                                    min={0}
                                    onChange={(e) =>
                                      updateVariantPrice(index, e.target.value)
                                    }
                                    className="border border-gray-300 rounded-lg p-2 text-sm shadow-sm focus:ring focus:ring-blue-300 focus:outline-none"
                                    disabled={!variant.active || !isEditMode}
                                  />
                                </div>
                              </div>
                              {isEditMode && (
                                <button
                                  onClick={() => deleteVariant(index)}
                                  className="bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600 flex items-center gap-2"
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                          </li>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
            <div className="flex justify-end mt-6">
              {isEditMode ? (
                <button
                  onClick={saveSKUs}
                  className="bg-blue-600 text-white px-4 py-2 rounded-full shadow hover:bg-blue-700 flex items-center gap-2 text-md"
                >
                  <FaSave /> Save SKUs
                </button>
              ) : (
                <button
                  onClick={toggleEditMode}
                  className="bg-yellow-500 text-white px-4 py-2 rounded-full shadow hover:bg-yellow-600 flex items-center gap-2 text-md"
                >
                  <FaEdit /> Edit SKUs
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SKUVariantManagement;
